/**
 * Run writes concurrently, wait for EVERY one to finish, then rethrow the first
 * failure.
 *
 * WHY NOT `Promise.all`. It rejects the instant one promise does, and leaves its
 * siblings running — unwatched and uncancellable. For a read that is merely
 * wasteful; for a WRITE it is a trap (PR #161 review). The booking save closes
 * its pane and reports "reopen the booking and try again" while another payment
 * request is still in the air. Reopen promptly, retype the figure, save — and the
 * original request lands afterwards under a different replay key, appending the
 * same payment twice. The user is invited to cause the duplicate by the very
 * message telling them something went wrong.
 *
 * Waiting for all of them costs nothing: they were already running together, so
 * the wall-clock is the slowest one either way. It only delays the BAD news, and
 * only until the truth is actually known.
 *
 * The FIRST rejection is the one rethrown — in the order the work was given, not
 * the order the failures arrived, so the message a user sees does not depend on
 * which request happened to time out first.
 *
 * …unless the caller says some rejections OUTRANK others. When a session
 * expires, EVERY in-flight request 401s at once, so a save whose first write
 * failed for an ordinary reason and whose second 401'd would, by work order
 * alone, surface the ordinary failure — and its toast lands over the redirect to
 * login, about a save that is void either way. `prefer` names what outranks
 * what; this helper stays ignorant of ApiError and of what the caller means.
 */
export async function allSettledOrThrow<T>(
  work: readonly Promise<T>[],
  prefer?: (reason: unknown) => boolean,
): Promise<T[]> {
  const settled = await Promise.allSettled(work);
  const rejected = settled.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (rejected.length > 0) {
    throw (prefer && rejected.find((r) => prefer(r.reason)))?.reason ?? rejected[0]!.reason;
  }
  return settled.map((r) => (r as PromiseFulfilledResult<T>).value);
}
